import {
  createContext,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n/useLanguage.js';

export interface DoorTransitionContextValue {
  /** Plays a door-opening flourish, then navigates to `path`. */
  enter(path: string, houseName: string): void;
}

const DoorTransitionContext = createContext<DoorTransitionContextValue | null>(null);

/** jsdom (the test environment) has no real `matchMedia`. */
function prefersReducedMotion(): boolean {
  try {
    return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

// Kept in step with the CSS: the scene sits still for a beat, then each door
// leaf swings open in turn — left first, then right, never both at once —
// then holds fully open for a beat before `.door-overlay--leaving` fades it
// all out. Navigation fires while the second leaf is swinging, so the new
// page is already there, glimpsed through the widening gap.
const DOOR_PAUSE_MS = 1500;
const DOOR_SWING_MS = 1000;
const SECOND_DOOR_DELAY_MS = DOOR_PAUSE_MS + DOOR_SWING_MS;
const NAVIGATE_AT_MS = SECOND_DOOR_DELAY_MS + 400;
const HOLD_MS = 1500;
const OVERLAY_FADE_MS = 550;
const BOTH_DOORS_OPEN_MS = SECOND_DOOR_DELAY_MS + DOOR_SWING_MS;

interface EnteringState {
  readonly houseName: string;
  readonly phase: 'opening' | 'leaving';
}

/**
 * A door that opens on the way into a house — purely a flourish, so it never
 * blocks navigation: a reduced-motion reader (or a test) gets the plain,
 * instant `navigate()` this all sits on top of.
 */
export function DoorTransitionProvider({ children }: { children: ReactNode }): ReactElement {
  const navigate = useNavigate();
  const t = useTranslation();
  const [entering, setEntering] = useState<EnteringState | null>(null);

  const enter = useCallback(
    (path: string, houseName: string) => {
      if (prefersReducedMotion()) {
        navigate(path);
        return;
      }
      setEntering({ houseName, phase: 'opening' });
      window.setTimeout(() => navigate(path), NAVIGATE_AT_MS);
      window.setTimeout(() => {
        setEntering((current) => (current === null ? null : { ...current, phase: 'leaving' }));
      }, BOTH_DOORS_OPEN_MS + HOLD_MS);
      window.setTimeout(
        () => {
          setEntering(null);
        },
        BOTH_DOORS_OPEN_MS + HOLD_MS + OVERLAY_FADE_MS,
      );
    },
    [navigate],
  );

  const value = useMemo<DoorTransitionContextValue>(() => ({ enter }), [enter]);

  return (
    <DoorTransitionContext.Provider value={value}>
      {children}
      {entering === null ? null : (
        <div
          className={`door-overlay door-overlay--opening${
            entering.phase === 'leaving' ? ' door-overlay--leaving' : ''
          }`}
          aria-hidden="true"
        >
          <span className="door-orb" />
          <span className="door-cloud door-cloud--1" />
          <span className="door-cloud door-cloud--2" />
          <Bird className="door-bird door-bird--1" />
          <Bird className="door-bird door-bird--2" />
          <Bird className="door-bird door-bird--3" />
          <Bird className="door-bird door-bird--4" />
          <Bird className="door-bird door-bird--5" />
          <span className="door-ground" />
          <span className="door-road" />
          <span className="door-stone door-stone--1" />
          <span className="door-stone door-stone--2" />
          <span className="door-stone door-stone--3" />
          <span className="door-bush door-bush--1" />
          <span className="door-bush door-bush--2" />
          <span className="door-bush door-bush--large door-bush--3" />
          <span className="door-bush door-bush--large door-bush--4" />
          <Tree className="door-tree door-tree--1" />
          <Tree className="door-tree door-tree--2" />
          <Tree className="door-tree door-tree--3" />
          <div className="door-pets">
            <Cat className="door-cat" />
            <Dog className="door-dog" />
          </div>
          <span className="door-mat" />
          <div className="door-house">
            <span className="door-roof" />
            <div className="door-wall">
              <span className="door-plaque">{entering.houseName}</span>

              <div className="door-window door-window--left">
                <span className="door-window__pane" />
                <FlowerBox className="door-flowerbox" />
              </div>

              {/* Sits behind the doors (painted first, covered while closed)
                  but outside their frame, so the greeting bubble isn't
                  clipped to the doorway once it swings open. */}
              <div className="door-character">
                <div className="door-character__inner">
                  <Character className="door-character__figure" />
                  <span className="door-speech-bubble">{t.doorTransition.greeting}</span>
                </div>
              </div>

              <div className="door-frame">
                <div className="door-panel">
                  <DoorHandle />
                </div>
                <div className="door-panel">
                  <DoorHandle />
                </div>
              </div>

              <div className="door-window door-window--right">
                <span className="door-window__pane" />
                <span className="door-shutter door-shutter--left" />
                <span className="door-shutter door-shutter--right" />
                <Drape className="door-drape" />
              </div>
            </div>
          </div>
        </div>
      )}
    </DoorTransitionContext.Provider>
  );
}

export function useDoorTransition(): DoorTransitionContextValue {
  const value = useContext(DoorTransitionContext);
  if (value === null) {
    throw new Error('useDoorTransition must be used inside <DoorTransitionProvider>');
  }
  return value;
}

function DoorHandle(): ReactElement {
  return (
    <svg viewBox="0 0 24 24">
      <circle cx="17" cy="12" r="1.6" fill="currentColor" />
    </svg>
  );
}

/** The innkeeper, waiting just inside the door, waving hello. */
function Character({ className }: { className: string }): ReactElement {
  return (
    <svg viewBox="0 0 40 90" className={className}>
      <rect x={13} y={78} width={5} height={9} rx={2} fill="#3a2c1a" />
      <rect x={22} y={78} width={5} height={9} rx={2} fill="#3a2c1a" />
      <path d="M9 78 Q8 50 20 48 Q32 50 31 78 Z" fill="#7a4b26" />
      <rect x={6} y={54} width={6} height={19} rx={3} fill="#e0a877" />
      <g className="door-character__wave">
        <rect x={28} y={33} width={6} height={19} rx={3} fill="#e0a877" />
        <circle cx={31} cy={31} r={4.2} fill="#e0a877" />
      </g>
      <circle cx={20} cy={36} r={11} fill="#e0a877" />
      <path d="M9 32 Q20 18 31 32 Q20 26 9 32 Z" fill="#5a3a22" />
      <circle cx={16} cy={36} r={1.1} fill="#2a2a2a" />
      <circle cx={24} cy={36} r={1.1} fill="#2a2a2a" />
      <path
        d="M15 41 Q20 45 25 41"
        stroke="#2a2a2a"
        strokeWidth={1.3}
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Tree({ className }: { className: string }): ReactElement {
  return (
    <svg viewBox="0 0 60 76" className={className}>
      <rect x={26} y={46} width={8} height={30} rx={3} fill="var(--border-strong)" />
      <circle cx={16} cy={38} r={22} fill="var(--inn-garden-leaf-soft)" />
      <circle cx={44} cy={40} r={20} fill="var(--inn-garden-leaf-soft)" />
      <circle cx={30} cy={22} r={24} fill="var(--inn-garden-leaf)" />
    </svg>
  );
}

function FlowerBox({ className }: { className: string }): ReactElement {
  return (
    <svg viewBox="0 -6 40 24" className={className}>
      <rect x={2} y={8} width={36} height={9} rx={2} fill="var(--border-strong)" />
      <line x1={8} y1={8} x2={6} y2={2} stroke="var(--inn-garden-leaf)" strokeWidth={2} />
      <line x1={20} y1={8} x2={20} y2={0} stroke="var(--inn-garden-leaf)" strokeWidth={2} />
      <line x1={31} y1={8} x2={33} y2={3} stroke="var(--inn-garden-leaf)" strokeWidth={2} />
      <circle cx={6} cy={1} r={3} fill="var(--danger)" />
      <circle cx={20} cy={-2} r={3} fill="var(--warning)" />
      <circle cx={33} cy={2} r={3} fill="var(--accent)" />
    </svg>
  );
}

function Drape({ className }: { className: string }): ReactElement {
  return (
    <svg viewBox="0 0 34 40" className={className} fill="none">
      <path
        d="M2 2 Q4 14 10 20 Q18 26 30 24 Q20 30 22 38 Q10 34 6 24 Q0 14 2 2 Z"
        fill="var(--inn-curtain)"
        opacity={0.92}
      />
    </svg>
  );
}

function Bird({ className }: { className: string }): ReactElement {
  return (
    <svg viewBox="0 0 24 14" className={className} fill="none">
      <path
        d="M1 8 Q6 0 12 7 Q18 0 23 8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** White with black patches, tail up, on the prowl. */
function Cat({ className }: { className: string }): ReactElement {
  return (
    <svg viewBox="0 0 48 28" className={className}>
      <path d="M8 15 Q0 9 3 2 Q7 8 10 14 Z" fill="#f5f2e7" />
      <ellipse cx={22} cy={17} rx={15} ry={8} fill="#f5f2e7" />
      <circle cx={40} cy={10} r={7} fill="#f5f2e7" />
      <path d="M35 6 L33 0 L39 4 Z" fill="#f5f2e7" />
      <path d="M42 4 L45 -1 L46 5 Z" fill="#f5f2e7" />
      <ellipse cx={15} cy={14} rx={6} ry={4} fill="#2a2a2a" opacity={0.92} />
      <ellipse cx={39} cy={7} rx={3.4} ry={2.8} fill="#2a2a2a" opacity={0.92} />
      <circle cx={42.5} cy={9.5} r={0.9} fill="#2a2a2a" />
      <circle cx={46} cy={11} r={0.7} fill="#c98a7a" />
      <rect x={12} y={23} width={2.4} height={5} rx={1} fill="#f5f2e7" />
      <rect x={19} y={23} width={2.4} height={5} rx={1} fill="#f5f2e7" />
      <rect x={27} y={23} width={2.4} height={5} rx={1} fill="#2a2a2a" opacity={0.85} />
      <rect x={34} y={23} width={2.4} height={5} rx={1} fill="#f5f2e7" />
    </svg>
  );
}

/** Grey, floppy-eared, tail mid-wag. */
function Dog({ className }: { className: string }): ReactElement {
  return (
    <svg viewBox="0 0 54 30" className={className}>
      <path
        d="M9 18 Q2 13 4 6"
        stroke="#8f8f8f"
        strokeWidth={3.4}
        strokeLinecap="round"
        fill="none"
      />
      <ellipse cx={24} cy={19} rx={16} ry={8.5} fill="#a8a8a8" />
      <circle cx={41} cy={12} r={7.5} fill="#a8a8a8" />
      <rect x={45} y={11} width={8} height={6} rx={3} fill="#a8a8a8" />
      <path d="M35 7 Q30 12 34 19 Q39 14 38 7 Z" fill="#8a8a8a" />
      <circle cx={43} cy={10} r={1} fill="#2a2a2a" />
      <circle cx={52} cy={13.5} r={1} fill="#2a2a2a" />
      <rect x={13} y={25} width={2.6} height={5.5} rx={1.1} fill="#8f8f8f" />
      <rect x={21} y={25} width={2.6} height={5.5} rx={1.1} fill="#8f8f8f" />
      <rect x={31} y={25} width={2.6} height={5.5} rx={1.1} fill="#8f8f8f" />
      <rect x={39} y={25} width={2.6} height={5.5} rx={1.1} fill="#8f8f8f" />
    </svg>
  );
}
